import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Modal, Button, Form, notification, Popconfirm, Select, Checkbox, Table, Tag, Divider, Input, InputNumber, Switch, Row, Col, Icon } from "antd";
import { getFoodItemsAdmin, addFoodItemAdmin, updateFoodItemAdmin, deleteFoodItemAdmin, getVendorsAdmin } from "../../redux/actions/foodItemsAdminAction";
import ImageCropperModal from "./ImageCropperModal";

const { Option } = Select;

const WEEK_OPTIONS = [
  { label: "W1", value: 1 },
  { label: "W2", value: 2 },
  { label: "W3", value: 3 },
  { label: "W4", value: 4 },
  { label: "W5", value: 5 },
];

const DAY_OPTIONS = [
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
];

const LOCATION_OPTIONS = [
  { label: "Noida", value: 1 },
  { label: "Lucknow", value: 2 },
  { label: "Indore", value: 3 },
  { label: "Remote", value: 6 },
  { label: "Other", value: 7 },
];

const emptyForm = {
  name: "",
  description: " ",
  price: "",
  isAvailable: true,
  selectedVendorId: "",
  availableWeeks: [],
  officeLocation: "",
  availableDays: [],
};

const AdminFoodItems = ({
  foodItems,
  total,
  loading,
  vendors,
  vendorsLoading,
  mutating,
  getFoodItemsAdmin,
  addFoodItemAdmin,
  updateFoodItemAdmin,
  deleteFoodItemAdmin,
  getVendorsAdmin,
}) => {
  const [locationFilter, setLocationFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);

  // Phase 15: Image Cropping State
  const [cropperOpen, setCropperOpen] = useState(false);
  const [selectedImageSrc, setSelectedImageSrc] = useState(null);
  const [croppedBlob, setCroppedBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    getVendorsAdmin();
    fetchItems();
    // eslint-disable-next-line
  }, []);

  const fetchItems = (loc, vid) => {
    const params = new URLSearchParams();
    if (loc || locationFilter) params.append("officeLocation", loc !== undefined ? loc : locationFilter);
    if (vid || vendorFilter) params.append("vendorId", vid !== undefined ? vid : vendorFilter);
    getFoodItemsAdmin(params.toString());
  };

  const handleLocationChange = (val) => {
    setLocationFilter(val);
    fetchItems(val, vendorFilter);
  };

  const handleVendorFilterChange = (val) => {
    setVendorFilter(val);
    fetchItems(locationFilter, val);
  };

  const openAddModal = () => {
    setEditItem(null);
    setForm(emptyForm);
    setPreviewUrl(null);
    setCroppedBlob(null);
    setModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditItem(item);
    setForm({
      name: item.name || "",
      description: item.description || "",
      price: item.price || "",
      isAvailable: parseInt(item.status || item.isAvailable) === 1,
      selectedVendorId: item.vendorId || item.vendorDetail?.vendorId || "",
      availableWeeks: item.availableWeeks || [],
      officeLocation: item.officeLocation || "",
      availableDays: item.availableDays || [],
    });
    setPreviewUrl(item.images && item.images.length > 0 ? item.images[0] : null);
    setCroppedBlob(null);
    setModalOpen(true);
  };

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setSelectedImageSrc(reader.result);
        setCropperOpen(true);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const onCropSave = (blob) => {
    setCroppedBlob(blob);
    setPreviewUrl(URL.createObjectURL(blob));
    setCropperOpen(false);
  };

  const onCropCancel = () => {
    setCropperOpen(false);
    setSelectedImageSrc(null);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.price || !form.selectedVendorId || !form.officeLocation) {
      notification.error({ message: "Required Fields Missing", description: "Name, Price, Vendor, and Office Location are mandatory." });
      return;
    }

    let result;
    if (croppedBlob) {
      // Use FormData for multipart/form-data upload
      const formData = new FormData();
      Object.keys(form).forEach(key => {
        if (key === 'availableWeeks' || key === 'availableDays') {
          formData.append(key, JSON.stringify(form[key]));
        } else if (key === 'isAvailable') {
          const val = form[key] ? 1 : 2;
          formData.append("isAvailable", val);
          formData.append("status", val);
        } else {
          formData.append(key, form[key]);
        }
      });
      formData.append("images", croppedBlob, "food_item.jpg");

      if (editItem) {
        formData.append("foodId", editItem._id || editItem.foodId);
        // Backend update route expects usedImages
        const existingImages = editItem.images || [];
        formData.append("usedImages", JSON.stringify(existingImages));
        result = await updateFoodItemAdmin(formData);
      } else {
        result = await addFoodItemAdmin(formData);
      }
    } else {
      const val = form.isAvailable ? 1 : 2;
      const payload = {
        ...form,
        isAvailable: val,
        status: val
      };
      if (editItem) {
        payload.foodId = editItem._id || editItem.foodId;
        payload.usedImages = JSON.stringify(editItem.images || []);
        result = await updateFoodItemAdmin(payload);
      } else {
        result = await addFoodItemAdmin(payload);
      }
    }

    if (result?.success) {
      notification.success({
        message: editItem ? "Updated Successfully" : "Added Successfully",
        description: `Food item "${form.name}" has been ${editItem ? "updated" : "added"}.`
      });
      setModalOpen(false);
      setCroppedBlob(null);
      setPreviewUrl(null);
      fetchItems();
    } else {
      notification.error({ message: "Action Failed", description: result?.message || "Please check your network and try again." });
    }
  };

  const handleToggleAvailability = async (item, checked) => {
    // Both isAvailable (boolean) and status (1=Active, 2=Hidden) are used
    const payload = {
      foodId: item._id || item.foodId,
      isAvailable: checked ? true : false,
      status: checked ? 1 : 2
    };

    const result = await updateFoodItemAdmin(payload);
    if (result?.success) {
      notification.success({
        message: "Status Updated",
        description: `"${item.name}" is now ${checked ? "Active" : "Hidden"}.`
      });
      fetchItems();
    } else {
      notification.error({
        message: "Update Failed",
        description: result?.message || "Could not update availability."
      });
    }
  };

  const handleDelete = async (item) => {
    const params = new URLSearchParams();
    params.append("foodId", item._id || item.foodId);
    const result = await deleteFoodItemAdmin(params.toString());
    if (result?.success) {
      notification.success({ message: "Deleted", description: "Food item has been removed." });
      fetchItems();
    } else {
      notification.error({ message: "Delete Failed", description: "Could not remove the food item." });
    }
  };

  const vendorName = (vendorId) => {
    if (!Array.isArray(vendors)) return "—";
    const v = vendors.find((vs) => (vs.vendorId || vs._id) === vendorId);
    return v ? v.name || v.firstName : "—";
  };

  return (
    <div className="body_container marginTop" style={{ padding: '24px', backgroundColor: '#f8f9fa', minHeight: 'calc(100vh - 64px)' }}>
      <style>{`
        .admin-food-table .ant-table-thead > tr > th {
          background-color: #1890ff !important;
          color: white !important;
          font-weight: 600 !important;
          border-bottom: none !important;
        }
        .admin-food-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f0f0f0 !important;
          padding: 16px 24px !important;
          vertical-align: middle !important;
          background: white !important;
        }
        .admin-food-table .ant-table-tbody > tr:hover > td {
          background: #fafafa !important;
        }
        .admin-food-table .ant-table-placeholder {
          background: white !important;
          border-bottom: 1px solid #f0f0f0 !important;
        }
        .admin-food-table .ant-pagination {
          margin: 16px 24px !important;
        }
      `}</style>
      <div style={{ background: '#fff', padding: '20px 24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '24px' }}>
        <Row type="flex" justify="space-between" align="middle">
          <Col>
            <h3 style={{ margin: 0, color: '#1a3353', fontWeight: 600 }}>Food Management Dashboard</h3>
            <p style={{ margin: 0, color: '#8c8c8c' }}>Admin panel for managing menu items and availability</p>
          </Col>
          <Col>
            <Button
              type="primary"
              size="large"
              onClick={openAddModal}
              style={{ borderRadius: '8px', fontWeight: 600, boxShadow: '0 4px 12px rgba(24, 144, 255, 0.2)' }}
            >
              <Icon type="plus" /> Add New Item
            </Button>
          </Col>
        </Row>
      </div>

      <div style={{ background: '#fff', padding: '20px 24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon type="filter" style={{ color: '#8c8c8c' }} />
            <span style={{ fontWeight: 500, color: '#595959' }}>Quick Filters:</span>
          </div>
          <Select
            placeholder="Office Location"
            allowClear
            onChange={handleLocationChange}
            value={locationFilter || undefined}
            style={{ borderRadius: '8px', width: 200, backgroundColor: '#f0f2f5' }}
          >
            {(LOCATION_OPTIONS || []).map((loc) => (
              <Option key={loc.value} value={loc.value}>{loc.label}</Option>
            ))}
          </Select>
          <Select
            placeholder="Filter by Vendor"
            allowClear
            onChange={handleVendorFilterChange}
            value={vendorFilter || undefined}
            loading={vendorsLoading}
            style={{ borderRadius: '8px', width: 240, backgroundColor: '#f0f2f5' }}
          >
            {(Array.isArray(vendors) ? vendors : []).map((v) => (
              <Option key={v.vendorId || v._id} value={v.vendorId || v._id}>
                {v.name || v.firstName} {v.status === 0 ? "(Inactive)" : ""}
              </Option>
            ))}
          </Select>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'visible', paddingBottom: '60px' }}>
        <Table
          className="admin-food-table"
          columns={[
            {
              title: "Item Details",
              key: "item",
              render: (_, record) => (
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  {record.images && record.images.length > 0 ? (
                    <img
                      src={record.images[0]}
                      alt={record.name}
                      style={{ width: "48px", height: "48px", borderRadius: "8px", objectFit: "cover", border: "1px solid #f0f0f0" }}
                    />
                  ) : (
                    <div style={{ width: "48px", height: "48px", borderRadius: "8px", backgroundColor: "#f5f5f5", display: "flex", alignItems: "center", justify: "center", border: "1px solid #f0f0f0" }}>
                      <Icon type="picture" style={{ fontSize: "20px", color: "#bfbfbf" }} />
                    </div>
                  )}
                  <div>
                    <div style={{ fontWeight: 600, color: '#1a3353', fontSize: '15px' }}>{record.name || "Unnamed"}</div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c', maxWidth: '240px' }} className="text-truncate">
                      {record.description || "No description provided."}
                    </div>
                  </div>
                </div>
              ),
            },
            {
              title: "Price",
              dataIndex: "price",
              key: "price",
              width: 100,
              render: (price) => (
                <span style={{ fontWeight: 700, color: '#2f54eb' }}>₹{price || 0}</span>
              ),
            },
            {
              title: "Vendor",
              key: "vendor",
              render: (_, record) => {
                const name = record.vendorDetail?.name || vendorName(record.vendorId);
                return <Tag style={{ borderRadius: '4px', border: 'none', background: '#f0f5ff', color: '#1d39c4' }}>{name || "—"}</Tag>;
              }
            },
            {
              title: "Location",
              key: "location",
              render: (_, record) => {
                const loc = LOCATION_OPTIONS.find(l => l.value === parseInt(record.officeLocation));
                return <Tag style={{ borderRadius: '4px', border: 'none', background: '#e6fffb', color: '#08979c' }}>{loc ? loc.label : "—"}</Tag>;
              },
            },
            {
              title: "Availability",
              key: "availability",
              width: 180,
              render: (_, record) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <div style={{ fontSize: '10px', color: '#bfbfbf', fontWeight: 600, textTransform: 'uppercase', marginBottom: '2px' }}>Weeks</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {(record.availableWeeks || []).length === 0 ? (
                        <span style={{ fontSize: '12px', color: '#ff4d4f' }}>None</span>
                      ) : (
                        (record.availableWeeks || []).map((w) => (
                          <Tag color="blue" key={w} style={{ margin: 0, borderRadius: '4px', fontSize: '11px' }}>W{w}</Tag>
                        ))
                      )}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '10px', color: '#bfbfbf', fontWeight: 600, textTransform: 'uppercase', marginBottom: '2px' }}>Days</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {(record.availableDays || []).length === 0 ? (
                        <Tag style={{ margin: 0, borderRadius: '4px', fontSize: '11px' }}>All Week</Tag>
                      ) : (
                        (record.availableDays || []).map((d) => {
                          const day = DAY_OPTIONS.find(opt => opt.value === d);
                          return <Tag color="cyan" key={d} style={{ margin: 0, borderRadius: '4px', fontSize: '11px' }}>{day ? day.label : d}</Tag>;
                        })
                      )}
                    </div>
                  </div>
                </div>
              ),
            },
            {
              title: "Status",
              dataIndex: "isAvailable",
              key: "status",
              width: 140,
              render: (isAvailable, record) => {
                const currentStatus = parseInt(record.status || isAvailable);
                const isActive = currentStatus === 1;
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Switch
                      size="small"
                      checked={isActive}
                      onChange={(checked) => handleToggleAvailability(record, checked)}
                    />
                    <span style={{
                      fontSize: '13px',
                      fontWeight: 500,
                      color: isActive ? '#52c41a' : '#ff4d4f'
                    }}>
                      {isActive ? "Active" : "Hidden"}
                    </span>
                  </div>
                );
              },
            },
            {
              title: "Actions",
              key: "actions",
              width: 100,
              render: (_, record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    type="link"
                    icon="edit"
                    onClick={() => openEditModal(record)}
                    style={{ padding: 0, textDecoration: 'none' }}
                  />
                  <Popconfirm
                    title="Delete this food item?"
                    onConfirm={() => handleDelete(record)}
                    okText="Yes"
                    cancelText="No"
                    placement="left"
                  >
                    <Button type="link" icon="delete" style={{ padding: 0, color: '#ff4d4f', textDecoration: 'none' }} />
                  </Popconfirm>
                </div>
              ),
            },
          ]}
          dataSource={Array.isArray(foodItems) ? foodItems : []}
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            position: 'bottom',
            hideOnSinglePage: true
          }}
          rowKey={(record) => record._id || record.foodId || Math.random()}
        />
      </div>

      <Modal
        title={
          <div style={{ padding: '4px 8px' }}>
            <h4 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1a3353' }}>
              {editItem ? "Modify Food Item" : "Create New Menu Item"}
            </h4>
          </div>
        }
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={720}
        centered
        destroyOnClose
        bodyStyle={{ padding: '32px 40px' }}
      >
        <Form layout="vertical">
          <Row gutter={32}>
            <Col span={14}>
              <Form.Item label={<strong>Item Name</strong>} required style={{ marginBottom: '24px' }}>
                <Input
                  size="large"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Masala Dosa"
                  style={{ borderRadius: '8px', height: '48px' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={<strong>Vendor Mapping</strong>} required style={{ marginBottom: '24px' }}>
                <Select
                  size="large"
                  placeholder="Select Vendor"
                  value={form.selectedVendorId || undefined}
                  onChange={(val) => setForm({ ...form, selectedVendorId: val })}
                  loading={vendorsLoading}
                  style={{ width: "100%", borderRadius: '8px' }}
                >
                  {(Array.isArray(vendors) ? vendors : []).map((v) => (
                    <Option key={v.vendorId || v._id} value={v.vendorId || v._id}>
                      {v.name || v.firstName} {v.status === 2 ? "(Disabled)" : ""}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item label={<strong>Office Location</strong>} required style={{ marginBottom: '24px' }}>
                <Select
                  size="large"
                  placeholder="Select Location"
                  value={form.officeLocation || undefined}
                  onChange={(val) => setForm({ ...form, officeLocation: val })}
                  style={{ width: "100%", borderRadius: '8px' }}
                >
                  {(LOCATION_OPTIONS || []).map((loc) => (
                    <Option key={loc.value} value={loc.value}>{loc.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={<strong>Description</strong>} style={{ marginBottom: '24px' }}>
            <Input.TextArea
              rows={4}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Brief summary of ingredients or dietary info..."
              style={{ borderRadius: '8px', padding: '12px' }}
            />
          </Form.Item>

          <Row gutter={32}>
            <Col span={12}>
              <Form.Item label={<strong>Pricing (₹)</strong>} required style={{ marginBottom: '24px' }}>
                <InputNumber
                  size="large"
                  min={0}
                  value={form.price}
                  onChange={(val) => setForm({ ...form, price: val })}
                  style={{ width: "100%", borderRadius: '8px', height: '48px', lineHeight: '48px' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={<strong>Status</strong>} style={{ marginBottom: '24px' }}>
                <div style={{
                  background: '#f9f9f9',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Switch
                      checked={form.isAvailable}
                      onChange={(checked) => setForm({ ...form, isAvailable: checked })}
                    />
                    <strong style={{ fontSize: '13px', color: form.isAvailable ? '#52c41a' : '#ff4d4f' }}>
                      {form.isAvailable ? "Active" : "Hidden"}
                    </strong>
                  </div>
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" style={{ margin: '32px 0 24px' }}>
            <span style={{ fontSize: '14px', color: '#8c8c8c', fontWeight: 500 }}>Availability Windows</span>
          </Divider>

          <Row gutter={32}>
            <Col span={12}>
              <Form.Item label={<strong>Recurrence Weeks</strong>} style={{ marginBottom: '24px' }}>
                <div style={{ background: '#f5f7fa', padding: '16px', borderRadius: '12px', minHeight: '80px' }}>
                  <Checkbox.Group
                    options={WEEK_OPTIONS}
                    value={form.availableWeeks}
                    onChange={(vals) => setForm({ ...form, availableWeeks: vals })}
                  />
                </div>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={<strong>Recurrence Days</strong>} style={{ marginBottom: '24px' }}>
                <div style={{ background: '#f5f7fa', padding: '16px', borderRadius: '12px', minHeight: '80px' }}>
                  <Checkbox.Group
                    options={DAY_OPTIONS}
                    value={form.availableDays}
                    onChange={(vals) => setForm({ ...form, availableDays: vals })}
                  />
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#8c8c8c' }}>
                    * Empty selection means available all week (Mon-Fri)
                  </div>
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '16px 0 32px' }} />

          <Row gutter={32}>
            <Col span={24}>
              <Form.Item label={<strong>Item Image (Square Recommended)</strong>}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                  <div
                    style={{
                      width: '120px',
                      height: '120px',
                      borderRadius: '16px',
                      border: '2px dashed #d9d9d9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      position: 'relative',
                      backgroundColor: '#fafafa',
                      transition: 'all 0.3s'
                    }}
                    onClick={() => document.getElementById('food-image-upload').click()}
                    onMouseOver={(e) => e.currentTarget.style.borderColor = '#1890ff'}
                    onMouseOut={(e) => e.currentTarget.style.borderColor = '#d9d9d9'}
                  >
                    {previewUrl ? (
                      <img src={previewUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ textAlign: 'center' }}>
                        <Icon type="picture" style={{ fontSize: '32px', color: '#bfbfbf' }} />
                        <div style={{ fontSize: '12px', color: '#bfbfbf', marginTop: '8px' }}>Upload Image</div>
                      </div>
                    )}
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'rgba(0,0,0,0.45)',
                      color: '#fff',
                      fontSize: '11px',
                      padding: '4px 0',
                      textAlign: 'center',
                      opacity: previewUrl ? 0 : 1,
                      transition: 'opacity 0.3s'
                    }}>
                      Click to Change
                    </div>
                    <input
                      id="food-image-upload"
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={onSelectFile}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', color: '#595959', fontWeight: 500, marginBottom: '4px' }}>
                      {croppedBlob ? "New Image Selected" : editItem && editItem.images && editItem.images.length > 0 ? "Current Image" : "No Image Uploaded"}
                    </div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                      <p style={{ margin: 0 }}>Supported formats: JPG, PNG, WEBP</p>
                      <p style={{ margin: 0 }}>Max size: 2MB. Square 1:1 aspect is ideal.</p>
                    </div>
                    {croppedBlob && (
                      <Tag color="cyan" style={{ marginTop: '12px', borderRadius: '4px', padding: '2px 8px' }}>
                        <Icon type="check-circle" theme="filled" style={{ marginRight: '6px' }} />
                        Image ready to be saved
                      </Tag>
                    )}
                  </div>
                </div>
              </Form.Item>
            </Col>
          </Row>

          <div style={{
            marginTop: '40px',
            textAlign: 'right',
            paddingTop: '32px',
            borderTop: '1px solid #f0f0f0',
            margin: '0 -40px -32px',
            padding: '24px 40px',
            background: '#fafafa',
            borderBottomLeftRadius: '12px',
            borderBottomRightRadius: '12px'
          }}>
            <Button
              size="large"
              onClick={() => setModalOpen(false)}
              style={{ marginRight: '16px', borderRadius: '8px', minWidth: '100px' }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              onClick={handleSubmit}
              loading={mutating}
              style={{ borderRadius: '8px', padding: '0 40px', fontWeight: 600, height: '48px' }}
            >
              {editItem ? "Save Changes" : "Create Item"}
            </Button>
          </div>
        </Form>
      </Modal>

      <ImageCropperModal
        visible={cropperOpen}
        imageSrc={selectedImageSrc}
        onCancel={onCropCancel}
        onSave={onCropSave}
      />
    </div>
  );
};

const BadgeStatus = ({ status, text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <span style={{
      display: 'inline-block',
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      backgroundColor: status === 'success' ? '#52c41a' : '#ff4d4f'
    }} />
    <span style={{ color: status === 'success' ? '#52c41a' : '#ff4d4f', fontWeight: 600, fontSize: '14px' }}>{text}</span>
  </div>
);

const mapStateToProps = (state) => ({
  foodItems: state.foodItemsAdmin?.foodItems || [],
  total: state.foodItemsAdmin?.total || 0,
  loading: state.foodItemsAdmin?.loading || false,
  vendors: state.foodItemsAdmin?.vendors || [],
  vendorsLoading: state.foodItemsAdmin?.vendorsLoading || false,
  mutating: state.foodItemsAdmin?.mutating || false,
});

const mapDispatchToProps = (dispatch) => ({
  getFoodItemsAdmin: (params) => dispatch(getFoodItemsAdmin(params)),
  addFoodItemAdmin: (params) => dispatch(addFoodItemAdmin(params)),
  updateFoodItemAdmin: (params) => dispatch(updateFoodItemAdmin(params)),
  deleteFoodItemAdmin: (params) => dispatch(deleteFoodItemAdmin(params)),
  getVendorsAdmin: (params) => dispatch(getVendorsAdmin(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdminFoodItems);
